import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/PhoneOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventAvailableIcon from "@mui/icons-material/EventAvailableOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassIcon from "@mui/icons-material/HourglassTopOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusyOutlined";
import PeopleIcon from "@mui/icons-material/PeopleAltOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonthOutlined";
import { format } from "date-fns";
import { getAppointmentsByBusiness } from "../../services/AppointmentService";
import { getAllBranches } from "../../services/BranchesService";
import { useUser } from "../../contexts/UserContext";
import { darken } from "@mui/material/styles";
import { BUSINESS_THEME, LABELS } from "../../platform/config";

/* Tokens (same as ClinicLayout), in the business type's colours. */
const C = {
  ink: BUSINESS_THEME.text,
  muted: BUSINESS_THEME.textSecondary,
  line: BUSINESS_THEME.border,
  page: BUSINESS_THEME.background,
  teal: BUSINESS_THEME.main,
  tealDark: darken(BUSINESS_THEME.main, 0.18),
  tealTint: BUSINESS_THEME.tint,
  seen: "#C9D6DA",
  cancelled: "#B4232A",
};

type Phase = "now" | "next" | "seen" | "cancelled";

interface Visit {
  id: string;
  start: Date;
  end: Date;
  status: string;
  phase: Phase;
  patientId?: string;
  patient: string;
  phone?: string;
  service: string;
  calendarId: string;
  calendar: string;
  price?: number;
}

const PAGE_SIZE = 100;
const MAX_PAGES = 10;

const dayParam = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const rowsOf = (resp: any): any[] => {
  if (Array.isArray(resp)) return resp;
  return resp?.appointments ?? resp?.content ?? resp?.data ?? [];
};

const fetchAll = async (businessId: string, viewType: "day" | "week", date: string) => {
  const all: any[] = [];
  for (let page = 0; page < MAX_PAGES; page++) {
    const rows = rowsOf(await getAppointmentsByBusiness(businessId, { viewType, date, page, size: PAGE_SIZE }));
    all.push(...rows);
    if (rows.length < PAGE_SIZE) break;
  }
  return all;
};

const toVisit = (raw: any, now: Date): Visit | null => {
  const start = new Date(raw?.start_at ?? raw?.startAt);
  const end = new Date(raw?.end_at ?? raw?.endAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  // Class tiles are group sessions, not a patient's visit.
  if (raw?.is_virtual || raw?.class_id) return null;
  const status = String(raw?.status ?? "");
  const cancelled = status === "CANCELLED" || status === "REFUNDED";
  const phase: Phase = cancelled
    ? "cancelled"
    : end <= now
      ? "seen"
      : start <= now
        ? "now"
        : "next";
  const client = raw?.client ?? {};
  const country = String(client?.country ?? "").replace(/\D/g, "");
  const phone = String(client?.phone ?? "").replace(/\D/g, "");
  return {
    id: String(raw?.id),
    start,
    end,
    status,
    phase,
    patientId: client?.id,
    patient: client?.full_name || raw?.client_name || "Walk-in",
    phone: phone ? `${country}${phone.replace(/^0+/, "")}` : undefined,
    service: raw?.service?.name || "Appointment",
    calendarId: String(raw?.unit?.id ?? raw?.unit_id ?? ""),
    calendar: raw?.unit?.name || raw?.unit_name || "",
    price: typeof raw?.total_price === "number" ? raw.total_price : undefined,
  };
};

const hm = (d: Date) => format(d, "HH:mm");

const minutesBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 60000);

const humanDuration = (mins: number) => {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
};

const greeting = (d: Date) => {
  const h = d.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

const card = {
  bgcolor: "#FFFFFF",
  border: `1px solid ${C.line}`,
  borderRadius: 3.5,
  boxShadow: "none",
} as const;

const CardTitle: React.FC<{ title: string; action?: React.ReactNode; hint?: string }> = ({
  title,
  action,
  hint,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: C.ink }}>{title}</Typography>
      {hint && <Typography sx={{ fontSize: "0.8rem", color: C.muted }}>{hint}</Typography>}
    </Box>
    {action}
  </Box>
);

const PHASE_CHIP: Record<Phase, { label: string; bg: string; fg: string; icon: React.ReactElement }> = {
  now: { label: "In consultation", bg: C.teal, fg: "#FFFFFF", icon: <HourglassIcon /> },
  next: { label: "Upcoming", bg: C.tealTint, fg: C.tealDark, icon: <AccessTimeIcon /> },
  seen: { label: "Seen", bg: "#EEF2F3", fg: C.muted, icon: <CheckCircleIcon /> },
  cancelled: { label: "Cancelled", bg: "#FBE9E9", fg: C.cancelled, icon: <EventBusyIcon /> },
};

/**
 * The clinic's home screen. Built around what a doctor and the front desk ask
 * all day: who is next, how full is each doctor's day, where is there a free
 * slot, and who did we just see. Everything comes from the day and week
 * appointment endpoints the schedule already uses.
 */
const ClinicToday: React.FC = () => {
  const { businessId = "" } = useParams<{ businessId: string }>();
  const base = `/dashboard/${businessId}`;
  const navigate = useNavigate();
  const { user } = useUser();
  const [now, setNow] = useState(() => new Date());
  const [calendars, setCalendars] = useState<{ id: string; name: string }[]>([]);
  const [todayRaw, setTodayRaw] = useState<any[]>([]);
  const [weekRaw, setWeekRaw] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState<string>("all");

  const load = useCallback(async () => {
    if (!businessId) return;
    const today = dayParam(new Date());
    try {
      const [units, day, week] = await Promise.all([
        getAllBranches(businessId),
        fetchAll(businessId, "day", today),
        fetchAll(businessId, "week", today),
      ]);
      const list = Array.isArray(units) ? units : units?.units ?? units?.data ?? [];
      setCalendars(
        list
          .filter((u: any) => u?.active !== false)
          .map((u: any) => ({ id: String(u.id), name: String(u.name ?? "") }))
      );
      setTodayRaw(day);
      setWeekRaw(week);
      setFailed(false);
    } catch {
      setFailed(true);
    }
    setLoading(false);
  }, [businessId]);

  useEffect(() => {
    load();
    // Countdowns and the "now" line move every 30 s; the data itself every 2 min.
    const tick = setInterval(() => setNow(new Date()), 30_000);
    const refresh = setInterval(load, 120_000);
    return () => {
      clearInterval(tick);
      clearInterval(refresh);
    };
  }, [load]);

  const visits = useMemo(
    () =>
      todayRaw
        .map((raw) => toVisit(raw, now))
        .filter((v): v is Visit => !!v)
        .sort((a, b) => a.start.getTime() - b.start.getTime()),
    [todayRaw, now]
  );
  const weekVisits = useMemo(
    () => weekRaw.map((raw) => toVisit(raw, now)).filter((v): v is Visit => !!v),
    [weekRaw, now]
  );

  const active = visits.filter((v) => v.phase !== "cancelled");
  const current = visits.filter((v) => v.phase === "now");
  const upcoming = visits.filter((v) => v.phase === "next");
  const seen = visits.filter((v) => v.phase === "seen");
  const cancelled = visits.filter((v) => v.phase === "cancelled");
  const patientsToday = new Set(active.map((v) => v.patientId ?? v.patient)).size;
  const nextVisit =
    upcoming[0] ??
    weekVisits
      .filter((v) => v.phase === "next" && v.start > now)
      .sort((a, b) => a.start.getTime() - b.start.getTime())[0];

  // Calendars with no visits today still get a lane, so a free doctor shows up.
  const lanes = useMemo(() => {
    const known = new Map(calendars.map((c) => [c.id, c.name]));
    visits.forEach((v) => !known.has(v.calendarId) && v.calendarId && known.set(v.calendarId, v.calendar));
    return Array.from(known, ([id, name]) => ({
      id,
      name,
      visits: visits.filter((v) => v.calendarId === id),
    }));
  }, [calendars, visits]);

  const shownAgenda = filter === "all" ? visits : visits.filter((v) => v.calendarId === filter);

  const firstName = String((user as any)?.fullName ?? "").split(" ")[0];

  if (loading) {
    return (
      <Box sx={{ display: "grid", gap: 2 }}>
        <Skeleton variant="rounded" height={60} sx={{ borderRadius: 3 }} />
        <Skeleton variant="rounded" height={170} sx={{ borderRadius: 3 }} />
        <Skeleton variant="rounded" height={260} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Greeting */}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, flexWrap: "wrap", mb: 2.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={{ fontSize: "0.9rem", color: C.muted, fontWeight: 600 }}>
            {format(now, "EEEE, d MMMM")}
          </Typography>
          <Typography
            component="h2"
            sx={{ fontWeight: 800, fontSize: { xs: "1.5rem", sm: "1.9rem" }, color: C.ink, lineHeight: 1.2 }}
          >
            {greeting(now)}
            {firstName ? `, ${firstName}` : ""}
          </Typography>
          <Typography sx={{ color: C.muted, mt: 0.5 }}>
            {active.length === 0
              ? "No appointments today."
              : `${active.length} appointment${active.length === 1 ? "" : "s"} today · ${
                  upcoming.length
                } still to come`}
          </Typography>
        </Box>
        {failed && (
          <Button variant="outlined" onClick={load}>
            Couldn't refresh — retry
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          // minmax(0, …) everywhere: a plain 1fr track grows to its widest
          // child's min-content, and the scrolling chip row would push the
          // whole page past a phone's edge.
          gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 2fr) minmax(320px, 1fr)" },
          alignItems: "start",
        }}
      >
        {/* Main column */}
        <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 2, minWidth: 0 }}>
          <UpNext
            current={current}
            next={nextVisit}
            now={now}
            onOpenPatient={(id) => navigate(`${base}/clients/details/${id}`)}
            onSchedule={() => navigate(`${base}/appointment`)}
            onNew={() => navigate(`${base}/appointment?new=1`)}
          />

          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" } }}>
            <Stat icon={<EventAvailableIcon />} label="Appointments" value={active.length} />
            <Stat icon={<CheckCircleIcon />} label="Seen" value={seen.length} />
            <Stat icon={<AccessTimeIcon />} label="Still to come" value={upcoming.length + current.length} />
            <Stat
              icon={<PeopleIcon />}
              label={`${LABELS.clients} today`}
              value={patientsToday}
              caption={cancelled.length ? `${cancelled.length} cancelled` : undefined}
            />
          </Box>

          <Paper sx={{ ...card, p: { xs: 2, sm: 2.5 } }}>
            <CardTitle
              title={`Today by ${LABELS.unit.toLowerCase()}`}
              hint="Each line is one day; the red line is now."
              action={
                <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => navigate(`${base}/appointment`)}>
                  Open schedule
                </Button>
              }
            />
            {lanes.length === 0 ? (
              <Typography sx={{ color: C.muted, fontSize: "0.9rem" }}>
                No {LABELS.units.toLowerCase()} yet. Add one from the dashboard to start booking.
              </Typography>
            ) : (
              <DayLanes lanes={lanes} now={now} />
            )}
          </Paper>

          <Paper sx={{ ...card, p: { xs: 2, sm: 2.5 } }}>
            <CardTitle title="Agenda" hint="Every appointment today, in order." />
            {lanes.length > 1 && (
              <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1.5, mb: 0.5, scrollbarWidth: "none" }}>
                {[{ id: "all", name: `All ${LABELS.units.toLowerCase()}` }, ...lanes].map((lane) => (
                  <Chip
                    key={lane.id}
                    label={lane.name}
                    clickable
                    color={filter === lane.id ? "primary" : "default"}
                    variant={filter === lane.id ? "filled" : "outlined"}
                    onClick={() => setFilter(lane.id)}
                    sx={{ height: 36, flexShrink: 0 }}
                  />
                ))}
              </Box>
            )}
            {shownAgenda.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CalendarMonthIcon sx={{ fontSize: 36, color: C.seen }} />
                <Typography sx={{ color: C.muted, mt: 1 }}>Nothing booked here today.</Typography>
                <Button sx={{ mt: 1 }} onClick={() => navigate(`${base}/appointment?new=1`)}>
                  Book an appointment
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)" }}>
                {shownAgenda.map((visit, index) => (
                  <AgendaRow
                    key={visit.id}
                    visit={visit}
                    first={index === 0}
                    showCalendar={filter === "all" && lanes.length > 1}
                    onOpen={() => visit.patientId && navigate(`${base}/clients/details/${visit.patientId}`)}
                  />
                ))}
              </Box>
            )}
          </Paper>
        </Box>

        {/* Side column */}
        <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 2, minWidth: 0 }}>
          <WeekLoad visits={weekVisits} now={now} />
          <RecentPatients
            visits={weekVisits}
            now={now}
            onOpen={(id) => navigate(`${base}/clients/details/${id}`)}
            onAll={() => navigate(`${base}/clients/clients`)}
          />
        </Box>
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------ */

const contactButtons = (visit: Visit) =>
  visit.phone ? (
    <>
      <Tooltip title="Call">
        <IconButton component="a" href={`tel:+${visit.phone}`} aria-label={`Call ${visit.patient}`} size="small">
          <PhoneIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="WhatsApp">
        <IconButton
          component="a"
          href={`https://wa.me/${visit.phone}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`WhatsApp ${visit.patient}`}
          size="small"
        >
          <WhatsAppIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  ) : null;

const UpNext: React.FC<{
  current: Visit[];
  next?: Visit;
  now: Date;
  onOpenPatient: (id: string) => void;
  onSchedule: () => void;
  onNew: () => void;
}> = ({ current, next, now, onOpenPatient, onSchedule, onNew }) => {
  const sameDay = next && next.start.toDateString() === now.toDateString();
  const wait = next ? minutesBetween(now, next.start) : 0;
  const when = !next
    ? ""
    : sameDay
      ? wait <= 0
        ? "Starting now"
        : `In ${humanDuration(wait)}`
      : format(next.start, "EEEE 'at' HH:mm");

  return (
    <Paper
      sx={{
        ...card,
        p: { xs: 2, sm: 3 },
        background: `linear-gradient(135deg, ${C.teal} 0%, ${C.tealDark} 100%)`,
        border: "none",
        color: "#FFFFFF",
      }}
    >
      {current.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: 2,
            pb: 2,
            borderBottom: "1px solid rgba(255,255,255,0.22)",
          }}
        >
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: "50%",
              bgcolor: "#7CF2C4",
              boxShadow: "0 0 0 4px rgba(124,242,196,0.25)",
            }}
          />
          <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
            With a {LABELS.client.toLowerCase()} now:
          </Typography>
          {current.map((v) => (
            <Typography key={v.id} sx={{ fontSize: "0.9rem", opacity: 0.92 }}>
              {v.patient} · {v.calendar} until {hm(v.end)}
            </Typography>
          ))}
        </Box>
      )}

      {next ? (
        <Box sx={{ display: "flex", gap: 2, alignItems: { xs: "flex-start", sm: "center" }, flexDirection: { xs: "column", sm: "row" } }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.8 }}>
              Up next · {when}
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.45rem", sm: "1.75rem" }, lineHeight: 1.25, mt: 0.5 }}>
              {next.patient}
            </Typography>
            <Typography sx={{ opacity: 0.9, mt: 0.25 }}>
              {hm(next.start)}–{hm(next.end)} · {next.service}
              {next.calendar ? ` · ${next.calendar}` : ""}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {next.phone && (
              <Button
                component="a"
                href={`https://wa.me/${next.phone}`}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsAppIcon />}
                sx={{ bgcolor: "rgba(255,255,255,0.16)", color: "#FFFFFF", "&:hover": { bgcolor: "rgba(255,255,255,0.26)" } }}
              >
                Remind
              </Button>
            )}
            {next.patientId && (
              <Button
                onClick={() => onOpenPatient(next.patientId!)}
                sx={{ bgcolor: "#FFFFFF", color: C.tealDark, "&:hover": { bgcolor: "#EAF7FA" } }}
              >
                Open {LABELS.client.toLowerCase()}
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.4rem" }}>Nothing else booked</Typography>
            <Typography sx={{ opacity: 0.9 }}>The rest of the week is open.</Typography>
          </Box>
          <Button onClick={onNew} sx={{ bgcolor: "#FFFFFF", color: C.tealDark, "&:hover": { bgcolor: "#EAF7FA" } }}>
            Book an appointment
          </Button>
          <Button onClick={onSchedule} sx={{ color: "#FFFFFF" }}>
            Open schedule
          </Button>
        </Box>
      )}
    </Paper>
  );
};

const Stat: React.FC<{ icon: React.ReactNode; label: string; value: number; caption?: string }> = ({
  icon,
  label,
  value,
  caption,
}) => (
  <Paper sx={{ ...card, p: 2 }}>
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 2.5,
        bgcolor: C.tealTint,
        color: C.teal,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 1,
        "& svg": { fontSize: 20 },
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontWeight: 800, fontSize: "1.6rem", color: C.ink, lineHeight: 1.1 }}>{value}</Typography>
    <Typography sx={{ fontSize: "0.82rem", color: C.muted }}>{label}</Typography>
    {caption && <Typography sx={{ fontSize: "0.74rem", color: C.cancelled, mt: 0.25 }}>{caption}</Typography>}
  </Paper>
);

/* One horizontal day per calendar: booked blocks, the now line, the next gap. */
const DayLanes: React.FC<{ lanes: { id: string; name: string; visits: Visit[] }[]; now: Date }> = ({
  lanes,
  now,
}) => {
  const all = lanes.flatMap((l) => l.visits);
  const dayStart = new Date(now);
  dayStart.setHours(Math.min(8, ...all.map((v) => v.start.getHours())), 0, 0, 0);
  const dayEnd = new Date(now);
  dayEnd.setHours(Math.max(18, ...all.map((v) => v.end.getHours() + (v.end.getMinutes() ? 1 : 0))), 0, 0, 0);
  const span = dayEnd.getTime() - dayStart.getTime();
  const pct = (d: Date) => Math.min(100, Math.max(0, ((d.getTime() - dayStart.getTime()) / span) * 100));
  const nowVisible = now >= dayStart && now <= dayEnd;

  const ticks: Date[] = [];
  for (let h = dayStart.getHours(); h <= dayEnd.getHours(); h += 2) {
    const t = new Date(dayStart);
    t.setHours(h);
    ticks.push(t);
  }

  const nextFree = (visits: Visit[]) => {
    const busy = visits
      .filter((v) => v.phase !== "cancelled")
      .sort((a, b) => a.start.getTime() - b.start.getTime());
    let cursor = now > dayStart ? new Date(now) : new Date(dayStart);
    // Round up to the next 5 minutes, the way a receptionist would offer it.
    cursor.setMinutes(Math.ceil(cursor.getMinutes() / 5) * 5, 0, 0);
    for (const v of busy) {
      if (v.end <= cursor) continue;
      if (minutesBetween(cursor, v.start) >= 15) break;
      if (v.end > cursor) cursor = new Date(v.end);
    }
    return cursor < dayEnd ? cursor : null;
  };

  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "180px minmax(0, 1fr)" }, columnGap: 2 }}>
        <Box />
        <Box sx={{ position: "relative", height: 18, display: { xs: "none", sm: "block" } }}>
          {ticks.map((t) => (
            <Typography
              key={t.getTime()}
              sx={{
                position: "absolute",
                left: `${pct(t)}%`,
                transform: "translateX(-50%)",
                fontSize: "0.72rem",
                color: C.muted,
              }}
            >
              {format(t, "HH:mm")}
            </Typography>
          ))}
        </Box>
      </Box>

      {lanes.map((lane) => {
        const booked = lane.visits
          .filter((v) => v.phase !== "cancelled")
          .reduce((sum, v) => sum + minutesBetween(v.start, v.end), 0);
        const free = nextFree(lane.visits);
        return (
          <Box
            key={lane.id}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "180px minmax(0, 1fr)" },
              columnGap: 2,
              rowGap: 0.75,
              alignItems: "center",
              py: 1.25,
              borderTop: `1px solid ${C.line}`,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: C.ink, fontSize: "0.92rem" }} noWrap>
                {lane.name}
              </Typography>
              <Typography sx={{ fontSize: "0.76rem", color: C.muted }}>
                {lane.visits.filter((v) => v.phase !== "cancelled").length} booked · {humanDuration(booked)}
                {free ? ` · free ${hm(free)}` : " · full"}
              </Typography>
            </Box>
            <Box
              sx={{
                position: "relative",
                height: 30,
                borderRadius: 2,
                bgcolor: C.page,
                border: `1px solid ${C.line}`,
                overflow: "hidden",
              }}
            >
              {lane.visits.map((v) => {
                const left = pct(v.start);
                const width = Math.max(0.8, pct(v.end) - left);
                const style =
                  v.phase === "now"
                    ? { bgcolor: C.teal }
                    : v.phase === "next"
                      ? { bgcolor: "#9ED3DF" }
                      : v.phase === "seen"
                        ? { bgcolor: C.seen }
                        : {
                            bgcolor: "transparent",
                            backgroundImage: `repeating-linear-gradient(45deg, ${C.cancelled}33 0 3px, transparent 3px 7px)`,
                          };
                return (
                  <Tooltip
                    key={v.id}
                    title={`${hm(v.start)}–${hm(v.end)} · ${v.patient} · ${v.service}${
                      v.phase === "cancelled" ? " (cancelled)" : ""
                    }`}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 3,
                        bottom: 3,
                        left: `${left}%`,
                        width: `${width}%`,
                        borderRadius: 1.25,
                        // 2px page-colour gap between back-to-back visits.
                        boxShadow: `0 0 0 1px ${C.page}`,
                        ...style,
                      }}
                    />
                  </Tooltip>
                );
              })}
              {nowVisible && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: `${pct(now)}%`,
                    width: 2,
                    bgcolor: "#E5484D",
                  }}
                />
              )}
            </Box>
          </Box>
        );
      })}

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1.5 }}>
        {[
          { label: "Seen", sx: { bgcolor: C.seen } },
          { label: "Now", sx: { bgcolor: C.teal } },
          { label: "Upcoming", sx: { bgcolor: "#9ED3DF" } },
          {
            label: "Cancelled",
            sx: { backgroundImage: `repeating-linear-gradient(45deg, ${C.cancelled}55 0 3px, transparent 3px 7px)`, border: `1px solid ${C.line}` },
          },
        ].map((item) => (
          <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 14, height: 10, borderRadius: 0.75, ...item.sx }} />
            <Typography sx={{ fontSize: "0.76rem", color: C.muted }}>{item.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const AgendaRow: React.FC<{ visit: Visit; first: boolean; showCalendar: boolean; onOpen: () => void }> = ({
  visit,
  first,
  showCalendar,
  onOpen,
}) => {
  const chip = PHASE_CHIP[visit.phase];
  const dim = visit.phase === "seen" || visit.phase === "cancelled";
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "64px minmax(0, 1fr) auto", sm: "92px minmax(0, 1fr) auto auto" },
        gap: { xs: 1, sm: 2 },
        alignItems: "center",
        py: 1.25,
        borderTop: first ? "none" : `1px solid ${C.line}`,
        ...(visit.phase === "now" && { bgcolor: C.tealTint, mx: -1.5, px: 1.5, borderRadius: 2 }),
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: 700, color: dim ? C.muted : C.ink, fontSize: "0.95rem" }}>
          {hm(visit.start)}
        </Typography>
        <Typography sx={{ fontSize: "0.74rem", color: C.muted }}>
          {minutesBetween(visit.start, visit.end)} min
        </Typography>
      </Box>
      <Box
        role={visit.patientId ? "button" : undefined}
        tabIndex={visit.patientId ? 0 : undefined}
        onClick={onOpen}
        onKeyDown={(e) => e.key === "Enter" && onOpen()}
        sx={{ minWidth: 0, cursor: visit.patientId ? "pointer" : "default" }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: dim ? C.muted : C.ink,
            textDecoration: visit.phase === "cancelled" ? "line-through" : "none",
          }}
          noWrap
        >
          {visit.patient}
        </Typography>
        <Typography sx={{ fontSize: "0.82rem", color: C.muted }} noWrap>
          {visit.service}
          {showCalendar && visit.calendar ? ` · ${visit.calendar}` : ""}
        </Typography>
      </Box>
      <Chip
        size="small"
        icon={chip.icon}
        label={chip.label}
        sx={{
          bgcolor: chip.bg,
          color: chip.fg,
          fontWeight: 600,
          "& .MuiChip-icon": { color: chip.fg, fontSize: 16 },
          display: { xs: "none", sm: "inline-flex" },
        }}
      />
      <Box sx={{ display: "flex", color: C.muted }}>{contactButtons(visit)}</Box>
    </Box>
  );
};

/* Appointments per day this week (Sun–Sat, as the backend counts weeks). */
const WeekLoad: React.FC<{ visits: Visit[]; now: Date }> = ({ visits, now }) => {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - start.getDay());
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const count = visits.filter(
      (v) => v.phase !== "cancelled" && v.start.toDateString() === d.toDateString()
    ).length;
    return { d, count, today: d.toDateString() === now.toDateString() };
  });
  const max = Math.max(1, ...days.map((d) => d.count));
  const total = days.reduce((s, d) => s + d.count, 0);

  return (
    <Paper sx={{ ...card, p: 2.5 }}>
      <CardTitle title="This week" hint={`${total} appointment${total === 1 ? "" : "s"}`} />
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 1, height: 130 }}>
        {days.map(({ d, count, today }) => (
          <Tooltip key={d.toISOString()} title={`${format(d, "EEEE d MMM")}: ${count}`}>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, height: "100%", justifyContent: "flex-end" }}>
              <Typography sx={{ fontSize: "0.76rem", fontWeight: 700, color: today ? C.teal : C.muted }}>
                {count}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 24,
                  height: `${Math.max(4, (count / max) * 88)}px`,
                  borderRadius: "4px 4px 0 0",
                  // Emphasis: today in the accent, the other days receding.
                  bgcolor: today ? C.teal : "#C5E4EC",
                }}
              />
              <Typography sx={{ fontSize: "0.74rem", color: today ? C.ink : C.muted, fontWeight: today ? 700 : 500 }}>
                {format(d, "EEE")}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Paper>
  );
};

const RecentPatients: React.FC<{
  visits: Visit[];
  now: Date;
  onOpen: (id: string) => void;
  onAll: () => void;
}> = ({ visits, now, onOpen, onAll }) => {
  const latest = new Map<string, Visit>();
  visits
    .filter((v) => v.phase === "seen" && v.patientId)
    .sort((a, b) => b.end.getTime() - a.end.getTime())
    .forEach((v) => !latest.has(v.patientId!) && latest.set(v.patientId!, v));
  const rows = Array.from(latest.values()).slice(0, 6);

  return (
    <Paper sx={{ ...card, p: 2.5 }}>
      <CardTitle
        title={`Recently seen`}
        hint="This week, latest first."
        action={
          <Button size="small" onClick={onAll}>
            All {LABELS.clients.toLowerCase()}
          </Button>
        }
      />
      {rows.length === 0 ? (
        <Typography sx={{ color: C.muted, fontSize: "0.9rem" }}>Nobody seen yet this week.</Typography>
      ) : (
        rows.map((v) => (
          <Box
            key={v.patientId}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(v.patientId!)}
            onKeyDown={(e) => e.key === "Enter" && onOpen(v.patientId!)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: 1,
              px: 1,
              mx: -1,
              borderRadius: 2,
              cursor: "pointer",
              "&:hover, &:focus-visible": { bgcolor: C.page },
            }}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: C.tealTint, color: C.teal, fontSize: "0.85rem", fontWeight: 700 }}>
              {initials(v.patient)}
            </Avatar>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography sx={{ fontWeight: 600, color: C.ink, fontSize: "0.92rem" }} noWrap>
                {v.patient}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: C.muted }} noWrap>
                {v.start.toDateString() === now.toDateString() ? `Today ${hm(v.start)}` : format(v.start, "EEE d MMM")} ·{" "}
                {v.service}
              </Typography>
            </Box>
            <ChevronRightIcon sx={{ color: C.muted }} />
          </Box>
        ))
      )}
    </Paper>
  );
};

export default ClinicToday;
